import { useTranslation } from "react-i18next";

import { ICallQueueStats } from "../../../../../models/CallQueue";
import classNames from "../../../../../utils/classNames";
import { formatCallTime, LiveCallPhase } from "../../../../../utils/liveCall";

function Progress({ label, value, target, barClassName }: { label: string; value: number; target: number; barClassName: string }) {
  const width = target > 0 ? Math.min(100, Math.round((value / target) * 100)) : 0;

  return (
    <div className="flex min-w-[120px] flex-col gap-1">
      <div className="flex justify-between text-xs text-gray-600">
        <span>{label}</span>
        <span className="font-semibold text-gray-900">{value} / {target}</span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-200" role="progressbar" aria-label={label} aria-valuenow={value} aria-valuemax={target}>
        <div className={classNames("h-1.5 rounded-full", barClassName)} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col text-xs text-gray-600">
      <span>{label}</span>
      <span className="text-sm font-semibold text-gray-900">{value}</span>
    </div>
  );
}

export function CallStatePill({ phase, seconds }: { phase: LiveCallPhase; seconds: number }) {
  const { t } = useTranslation("leads");

  const styles: Record<LiveCallPhase, { label: string; className: string; dot: string }> = {
    available: { label: t("Available"), className: "bg-gray-50 border-gray-200 text-gray-700", dot: "bg-gray-400" },
    ringing: { label: t("Ringing"), className: "bg-amber-50 border-amber-200 text-amber-800", dot: "bg-amber-500" },
    onCall: { label: `${t("On call")} · ${formatCallTime(seconds)}`, className: "bg-green-50 border-green-200 text-green-800", dot: "bg-green-600" },
    wrapUp: { label: t("Wrap-up"), className: "bg-amber-50 border-amber-200 text-amber-800", dot: "bg-amber-500" },
  };
  const style = styles[phase];

  return (
    <div className={classNames("flex items-center gap-2 rounded-full border px-3 py-1.5 text-[13px] font-semibold", style.className)} aria-live="polite">
      <span className={classNames("h-2 w-2 rounded-full", style.dot)} />
      <span className="tabular-nums">{style.label}</span>
    </div>
  );
}

export default function QuotesHeaderStrip({
  stats,
  phase,
  seconds,
}: {
  stats?: ICallQueueStats;
  phase: LiveCallPhase;
  seconds: number;
}) {
  const { t } = useTranslation("leads");

  return (
    <div className="flex flex-wrap items-center gap-5">
      <Progress label={t("Calls")} value={stats?.calls ?? 0} target={stats?.callTarget ?? 0} barClassName="bg-primary-600" />
      <Progress label={t("Bookings")} value={stats?.bookings ?? 0} target={stats?.bookingTarget ?? 0} barClassName="bg-green-600" />
      <Figure label={t("Conversion")} value={`${stats?.conversionPercent ?? 0}%`} />
      <Figure label={t("Queue")} value={`${(stats?.queueDue ?? 0).toLocaleString("en-GB")} ${t("due")}`} />
      <CallStatePill phase={phase} seconds={seconds} />
    </div>
  );
}
